import { lazy } from 'react';

// Sources:
//  https://gist.github.com/thepuskar/ca5ac392f7e7839375985fda9333281a
//  https://rollbar.com/blog/javascript-chunk-load-error/
export const lazyWithRetry = (
  componentImport: any // Cannot yet find a correct type for this
) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(window.localStorage.getItem('page-has-been-force-refreshed') || 'false');

    try {
      const component = await componentImport();

      window.localStorage.setItem('page-has-been-force-refreshed', 'false');

      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem('page-has-been-force-refreshed', 'true');
        return window.location.reload();
      }

      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      // Let's let the application crash and raise the error.
      throw error;
    }
  });

export const lazyLoadData = <T>(dataImport: () => Promise<T>): Promise<T> => {
  return dataImport()
    .then((jsonData) => {
      return jsonData; // Return the entire JSON data
    })
    .catch((error) => {
      logger.error('Failed to load JSON data:', { error });
      throw error;
    });
};

const configuredAssetCDNBaseURL = import.meta.env.VITE_ASSET_CDN_BASE_URL;
if (!configuredAssetCDNBaseURL && !import.meta.env.DEV) {
  throw new Error('Missing required environment variable VITE_ASSET_CDN_BASE_URL for non-development build.');
}
const assetCDNBaseURL = configuredAssetCDNBaseURL || 'static.dev.planera.io';
const normalizedAssetCDNBaseURL = assetCDNBaseURL.includes('://') ? assetCDNBaseURL : `https://${assetCDNBaseURL}`;

export const loadJSONData = async <T>(url: string): Promise<T> => {
  try {
    const response = await fetch(new URL(url, `${normalizedAssetCDNBaseURL.replace(/\/+$/, '')}/`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate', // Prevent caching
        Pragma: 'no-cache', // HTTP 1.0 backward compatibility
        Expires: '0' // Expire immediately
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to load data from ${url}: ${response.statusText}`);
    }
    const data: T = await response.json();
    return data;
  } catch (error) {
    logger.error('Failed to load JSON data:', { error });
    throw error;
  }
};
